import React, { useEffect, useState } from 'react';
import { FaCircleChevronRight } from 'react-icons/fa6';
import { LuChevronsLeft, LuChevronsRight } from 'react-icons/lu';

import { Tooltip } from 'antd';
import type { EditableSelectOption } from 'components/common/EditableCell';
import {
  PRODUCTION_MATRIX_COLUMN_LABEL_PRICE,
  PRODUCTION_MATRIX_COLUMN_LABEL_YIELD,
  PRODUCTION_MATRIX_ROW_LABEL_EXPECTED,
  PRODUCTION_MATRIX_ROW_LABEL_HIGH,
  PRODUCTION_MATRIX_ROW_LABEL_LOW,
  SUMMARY_DATA_BASIS_ACTUAL,
  SUMMARY_DATA_BASIS_LABELS,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SOIL_INPUT,
  TASKS_GRID_COLUMN_LABEL_COST_PER_HA,
  TASKS_GRID_COLUMN_LABEL_COST_PER_UNIT,
  TASKS_GRID_COLUMN_LABEL_INPUT_NAME,
  TASKS_GRID_COLUMN_LABEL_RATE,
  TASKS_GRID_COLUMN_LABEL_RATE_UNIT,
  TASKS_GRID_COLUMN_LABEL_REQUIRED,
  TASKS_GRID_COLUMN_LABEL_TASK,
  TASKS_SHEET_COLUMN_LABEL_TYPE,
  TASKS_SHEET_EMPTY_PLAN,
  TASKS_SHEET_EXPAND_TASK_COLUMN_LABEL,
  TASKS_SHEET_MOA_GROUP_PREFIX,
  TASKS_SHEET_NOTES_COLUMN_LABEL,
  TASKS_SHEET_TRIM_TASK_COLUMN_LABEL,
  TASKS_SHEET_WITHOUT_PREFIX,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import {
  IInputStep,
  IInputStepProduction,
  InputStepType,
} from 'interfaces/IInputStep';
import type { IInputTask, IInputTaskBase } from 'interfaces/IInputTask';
import { pxToRem } from 'utils/cssUnits';
import MergeClassNames from 'utils/MergeClassNames';

import { hasDuplicateStepIds } from './hasDuplicateStepIds';
import ProductionSheetRow, {
  PRODUCTION_SHEET_COLUMNS,
} from './ProductionSheetRow';
import { computeSheetLayout } from './sheetLayout';
import { getTaskSteps } from './stepCalculations';
import {
  ALL_STEP_CATEGORY_KEY,
  filterStepsByCategory,
  StepCategoryFilter,
} from './stepCategoryFilters';
import { StepSelection } from './stepFactory';
import { getStepFilterDetail } from './taskCaption';
import { ApplicationTaskFields } from './taskFactory';
import TaskSheetNotesRow, { TaskSheetNotesCell } from './TaskSheetNotesRow';
import TaskSheetTaskCell from './TaskSheetTaskCell';
import TaskSheetTotalRow from './TaskSheetTotalRow';
import type {
  TaskColumnWidth,
  TasksViewPreferences,
} from './tasksViewPreferences';
import TaskStepRow from './TaskStepRow';
import type {
  ContextMenuTarget,
  useStepContextMenu,
} from './useStepContextMenu';
import type { useStepReorder } from './useStepReorder';

type StepReorderApi = ReturnType<typeof useStepReorder>;
type StepContextMenuApi = ReturnType<typeof useStepContextMenu>;

// Each task's block opens with a heavier rule, like a group line in a
// spreadsheet, so where one task ends and the next starts is plain; and is a
// hover group, so the Task cell's "+ Add input" shows anywhere in its rows.
const TASK_BLOCK_CLASS = 'group/task border-t-2 border-t-gray-dark';

const HEADER_CELL_BASE_CLASS =
  'border border-gray-medium px-2 py-1.5 text-left text-sm font-semibold';

// A row of a task's block in the sheet, given the Task cell (and, on a wide
// sheet, the Notes cell) when it's the block's first row.
type SheetRowRenderer = (
  leadingCell?: React.ReactNode,
  trailingCell?: React.ReactNode
) => React.ReactNode;

interface TaskCategorySheetProps {
  /** The active filter; All shows every product of every task. */
  activeFilter: StepCategoryFilter;
  /** Tasks with a product in the category (and the task being entered). */
  planTasks: IInputTask[];
  /** Tasks the category can go in that have none of it yet. */
  missingTasks: IInputTask[];
  planInput: IInput;
  gUnits: GUnit[];
  areaUnitOptions: EditableSelectOption[];
  preferences: TasksViewPreferences;
  /** Tasks whose products are folded to one line of names. */
  collapsedTaskIds: string[];
  onToggleTaskCollapsed: (taskId: string) => void;
  onTaskColumnWidthChange: (width: TaskColumnWidth) => void;
  /**
   * Actual mode: the header row and Task column turn charcoal, with an
   * "Actual" tag, so it's unmistakable which is being edited (OP-2556).
   */
  isActual: boolean;
  /** Shown in place of the sheet's rows when the plan has no tasks. */
  emptyState: React.ReactNode;
  /** The organisation's default water rate, marked "default" where used. */
  orgDefaultWaterRate: number | null;
  /** Per task, the water rate to offer when it needs one and has none. */
  waterRateSuggestions: Map<string, number>;
  entryTaskId: string | null;
  justFinishedTaskId: string | null;
  openDetailsTaskId: string | null;
  setOpenDetailsTaskId: (taskId: string | null) => void;
  contextMenuTarget: ContextMenuTarget | null;
  /** The task whose product picker is open; its "+ Add input" stays in view. */
  activeSelectorTaskId: string | null;
  /**
   * The width (px) the sheet has, measured by its parent outside the
   * sheet's own width cap — measuring inside the cap would lock the layout
   * (Notes couldn't become a column again until a refresh). 0 if unmeasured.
   */
  availableWidth: number;
  /** The new task's popover "Add inputs": closes it and opens the product picker. */
  onEntryAddInputs: (taskId: string) => void;
  /** Switches to the Production filter (a Production task's Low–High range). */
  onShowProductionFilter?: () => void;
  /** The sheet's width cap, so the toolbar above can line up with it. */
  onMaxWidthClassChange?: (maxWidthClass: string) => void;
  /** The task's "+ Add" for the category, with the given label. */
  renderAddControl: (task: IInputTask, label?: string) => React.ReactNode;
  commitTaskField: (
    task: IInputTask,
    patch: Partial<IInputTaskBase & ApplicationTaskFields>
  ) => void;
  commitStepField: (
    task: IInputTask,
    stepId: string,
    patch: Partial<IInputStep>
  ) => void;
  onDateEditEnd: (taskId: string) => void;
  onTaskContextMenu: (event: React.MouseEvent, taskId: string) => void;
  onOpenMenuFromKebab: (
    event: React.MouseEvent<HTMLButtonElement>,
    target: ContextMenuTarget
  ) => void;
  editingStepInputId: string | null;
  setEditingStepInputId: (stepId: string | null) => void;
  handleStepInputChange: (
    task: IInputTask,
    stepId: string,
    selections: StepSelection[]
  ) => void;
  stepReorder: StepReorderApi;
  stepContextMenu: StepContextMenuApi;
}

// The Tasks grid's new layout (OP-2556): one spreadsheet, a row per product,
// with each task's Task cell spanning its rows, then the task's note and its
// total. All shows every product; a filter shows the category's plan, with
// the tasks the category could go in but doesn't yet collapsed below it, each
// with its own "+ Add". Production filtered lays the yield and price
// scenarios along the row. View options choose the columns, what the Task
// cell shows and which rows show. A collapsed task (its chevron, or Collapse
// all) folds its products to one line of names.
const TaskCategorySheet: React.FC<TaskCategorySheetProps> = ({
  activeFilter,
  planTasks,
  missingTasks,
  planInput,
  gUnits,
  areaUnitOptions,
  preferences,
  collapsedTaskIds,
  onToggleTaskCollapsed,
  onTaskColumnWidthChange,
  emptyState,
  isActual,
  orgDefaultWaterRate,
  waterRateSuggestions,
  entryTaskId,
  justFinishedTaskId,
  openDetailsTaskId,
  setOpenDetailsTaskId,
  contextMenuTarget,
  activeSelectorTaskId,
  onMaxWidthClassChange,
  onShowProductionFilter,
  onEntryAddInputs,
  availableWidth,
  renderAddControl,
  commitTaskField,
  commitStepField,
  onDateEditEnd,
  onTaskContextMenu,
  onOpenMenuFromKebab,
  editingStepInputId,
  setEditingStepInputId,
  handleStepInputChange,
  stepReorder,
  stepContextMenu,
}) => {
  const [isMissingOpen, setIsMissingOpen] = useState(false);
  const isAll = activeFilter.key === ALL_STEP_CATEGORY_KEY;
  const isProductionFilter = (activeFilter.stepTypes ?? []).includes(
    InputStepType.Production
  );
  // Measured on the Inputs area, not the window, since Live totals and the
  // tree share the screen with it (0 until measured).
  const { isStacked, isTrimmed, isCompact, columns, folded, notesAsColumn } =
    computeSheetLayout(
      availableWidth > 0 ? pxToRem(availableWidth) : 0,
      preferences,
      isProductionFilter
    );
  // Input name and Rate always show.
  const productColumnCount = isProductionFilter
    ? 1 + PRODUCTION_SHEET_COLUMNS.length
    : 2 + Object.values(columns).filter(Boolean).length;
  const columnCount =
    (isStacked ? 0 : 1) + productColumnCount + (notesAsColumn ? 1 : 0);
  // The plan is empty, so the tasks it could go in are what's worth showing.
  const showMissing = isMissingOpen || planTasks.length === 0;
  const { taskCell: taskCellFields, rows: rowOptions } = preferences;
  const headerCellClass = MergeClassNames(
    HEADER_CELL_BASE_CLASS,
    isActual ? 'bg-charcoal text-white' : 'bg-secondary-light text-charcoal'
  );

  // withAddControl is off where "+ Add input" sits elsewhere — across the
  // row of a task with no products, or in the total row — since two would
  // open two pickers. hasProductRows gives the cell its collapse chevron.
  const renderTaskCell = (
    task: IInputTask,
    rowSpan: number,
    { isMuted = false, withAddControl = true, hasProductRows = true } = {}
  ) => (
    <TaskSheetTaskCell
      task={task}
      rowSpan={isStacked ? 1 : rowSpan}
      // Stacked, the cell is a band across the product columns.
      colSpan={isStacked ? productColumnCount : undefined}
      gUnits={gUnits}
      planInput={planInput}
      areaUnitOptions={areaUnitOptions}
      fields={taskCellFields}
      isTrimmed={isTrimmed}
      isCompact={isCompact}
      isAddControlPinned={
        task.taskId === entryTaskId || task.taskId === activeSelectorTaskId
      }
      orgDefaultWaterRate={orgDefaultWaterRate}
      suggestedWaterRate={waterRateSuggestions.get(task.taskId) ?? null}
      isMuted={isMuted}
      isDark={isActual && !isMuted}
      isCollapsed={
        hasProductRows && !isMuted
          ? collapsedTaskIds.includes(task.taskId)
          : undefined
      }
      onToggleCollapsed={() => onToggleTaskCollapsed(task.taskId)}
      isDetailsOpen={openDetailsTaskId === task.taskId}
      onDetailsOpenChange={(open) =>
        setOpenDetailsTaskId(open ? task.taskId : null)
      }
      isMenuOpen={
        contextMenuTarget?.rowType === 'task' &&
        contextMenuTarget.taskId === task.taskId
      }
      addControl={withAddControl ? renderAddControl(task) : undefined}
      areNotesHidden={!rowOptions.notes}
      onAddInputs={
        task.taskId === entryTaskId
          ? () => onEntryAddInputs(task.taskId)
          : undefined
      }
      commitTaskField={commitTaskField}
      onDateEditEnd={onDateEditEnd}
      onContextMenu={onTaskContextMenu}
      onOpenMenuFromKebab={onOpenMenuFromKebab}
    />
  );

  // A task with no product in the category: its Task cell, then its "+ Add"
  // across the product columns (and its note, on a wide sheet). Stacked, the
  // Task cell is a band above the "+ Add".
  const renderEmptyTaskRow = (task: IInputTask, isMuted: boolean) => {
    const rowClassName = MergeClassNames(
      'group',
      task.taskId === entryTaskId && 'task-entry-row',
      task.taskId === justFinishedTaskId && 'task-row-just-finished'
    );
    const taskCell = renderTaskCell(task, 1, {
      isMuted,
      withAddControl: false,
      hasProductRows: false,
    });
    // A task the filter hides is named by the inputs it already has, so it
    // can be told apart from the others listed with it.
    const otherInputs = isMuted
      ? getTaskSteps(task)
          .map((step) => step.item?.trim())
          .filter(Boolean)
          .join(', ')
      : '';
    const addCell = (
      <td
        colSpan={productColumnCount}
        className={MergeClassNames(
          'border border-gray-medium px-2 py-1',
          isMuted ? 'bg-rowHover' : 'bg-white'
        )}
      >
        <span className="flex min-w-0 items-center gap-3">
          {renderAddControl(task, isAll ? undefined : activeFilter.label)}
          {otherInputs && (
            <span
              className="min-w-0 truncate text-sm text-gray-dark"
              title={otherInputs}
            >
              {otherInputs}
            </span>
          )}
        </span>
      </td>
    );
    const notesCell =
      notesAsColumn && !isMuted ? (
        <TaskSheetNotesCell task={task} commitTaskField={commitTaskField} />
      ) : notesAsColumn ? (
        <td className="border border-gray-medium bg-rowHover" />
      ) : null;
    if (isStacked) {
      return (
        <React.Fragment key={task.taskId}>
          <tr data-row-key={task.taskId} className={rowClassName}>
            {taskCell}
          </tr>
          <tr className={rowClassName}>{addCell}</tr>
        </React.Fragment>
      );
    }
    return (
      <tr key={task.taskId} data-row-key={task.taskId} className={rowClassName}>
        {taskCell}
        {addCell}
        {notesCell}
      </tr>
    );
  };

  const renderPlanTask = (task: IInputTask) => {
    const allSteps = getTaskSteps(task);
    const steps = filterStepsByCategory(allSteps, activeFilter.key);
    const blockClassName = MergeClassNames(
      task.taskId === entryTaskId && 'task-entry-row',
      task.taskId === justFinishedTaskId && 'task-row-just-finished'
    );

    if (steps.length === 0) {
      return (
        <tbody key={task.taskId} className={TASK_BLOCK_CLASS}>
          {renderEmptyTaskRow(task, false)}
        </tbody>
      );
    }

    const hasAmbiguousStepIds = hasDuplicateStepIds(allSteps);
    const reorderableSteps = steps.filter(
      (step) => step.stepType !== InputStepType.Production
    );
    const canReorder = reorderableSteps.length > 1 && !hasAmbiguousStepIds;
    const isTaskReordering = stepReorder.reorderTarget?.taskId === task.taskId;

    const rows: SheetRowRenderer[] = [];

    if (!collapsedTaskIds.includes(task.taskId)) {
      steps.forEach((step, index) => {
        rows.push((leadingCell, trailingCell) =>
          step.stepType === InputStepType.Production ? (
            <ProductionSheetRow
              key={step.stepId}
              task={task}
              step={step as IInputStepProduction}
              leadingCell={leadingCell}
              trailingCell={trailingCell}
              inlineColumns={isProductionFilter ? undefined : columns}
              onShowAllScenarios={onShowProductionFilter}
              editingStepInputId={editingStepInputId}
              setEditingStepInputId={setEditingStepInputId}
              handleStepInputChange={handleStepInputChange}
              commitStepField={commitStepField}
              stepContextMenu={stepContextMenu}
            />
          ) : (
            <TaskStepRow
              key={
                hasAmbiguousStepIds ? `${step.stepId}:${index}` : step.stepId
              }
              task={task}
              step={step}
              // Every row keeps its bottom border: the sheet is one grid.
              isLastRow={false}
              detailLine={getStepFilterDetail(
                step,
                activeFilter.key,
                TASKS_SHEET_MOA_GROUP_PREFIX
              )}
              leadingCell={leadingCell}
              trailingCell={trailingCell}
              sheetColumns={columns}
              foldedColumns={folded}
              hasAmbiguousStepIds={hasAmbiguousStepIds}
              canReorder={canReorder}
              gUnits={gUnits}
              editingStepInputId={editingStepInputId}
              setEditingStepInputId={setEditingStepInputId}
              handleStepInputChange={handleStepInputChange}
              commitStepField={commitStepField}
              stepReorder={stepReorder}
              stepContextMenu={stepContextMenu}
            />
          )
        );
      });
    } else {
      // Collapsed: the products by name, on one line.
      rows.push((leadingCell, trailingCell) => (
        <tr key="collapsed" className="bg-white">
          {leadingCell}
          <td
            colSpan={productColumnCount}
            className="border border-gray-medium px-2 py-1 text-sm text-gray-dark"
          >
            {steps
              .map((step) => step.item)
              .filter(Boolean)
              .join(' · ')}
          </td>
          {trailingCell}
        </tr>
      ));
    }

    // Like the Notes column, the row is always there to click into, empty
    // or not; a collapsed task keeps only a note it has.
    if (
      rowOptions.notes &&
      !notesAsColumn &&
      (task.notes || !collapsedTaskIds.includes(task.taskId))
    ) {
      rows.push((leadingCell) => (
        <TaskSheetNotesRow
          key="notes"
          task={task}
          leadingCell={leadingCell}
          colSpan={productColumnCount}
          commitTaskField={commitTaskField}
        />
      ));
    }

    // The total row carries $/ha, plus nutrients or Groups under those two
    // filters; with $/ha hidden elsewhere it would be empty, so it goes.
    const totalHasContent =
      columns.costPerHa ||
      activeFilter.key === SUMMARY_ENTITY_SOIL_INPUT ||
      activeFilter.key === SUMMARY_ENTITY_PLANT_PROTECTION;
    // A Production task gets one too, so its "+ Add input" for yield-linked
    // Operation costs sits under its rows like every other task's (OP-2399).
    const hasTotalRow =
      rowOptions.totals && totalHasContent && !isProductionFilter;
    if (hasTotalRow) {
      rows.push((leadingCell) => (
        <TaskSheetTotalRow
          key="total"
          task={task}
          steps={reorderableSteps}
          planInput={planInput}
          gUnits={gUnits}
          categoryKey={activeFilter.key}
          leadingCell={leadingCell}
          columns={columns}
          productColumnCount={productColumnCount}
          addControl={renderAddControl(task)}
          isAddControlPinned={
            task.taskId === entryTaskId || task.taskId === activeSelectorTaskId
          }
        />
      ));
    }

    // "+ Add input" sits in the total row, under the products; without one
    // (hidden by View options, or under the Production filter) it's in the
    // Task cell.
    const taskCell = renderTaskCell(task, rows.length, {
      withAddControl: !hasTotalRow,
    });
    const notesCell = notesAsColumn ? (
      <TaskSheetNotesCell
        task={task}
        rowSpan={rows.length}
        commitTaskField={commitTaskField}
      />
    ) : undefined;

    return (
      // Each task's rows are their own <tbody>, which hosts keyboard focus
      // for re-order mode as the steps wrapper does in the original layout.
      <tbody
        key={task.taskId}
        ref={stepReorder.registerStepsWrapper(task.taskId)}
        tabIndex={-1}
        onKeyDown={stepReorder.handleReorderKeyDown}
        onBlur={stepReorder.handleReorderBlur}
        data-row-key={task.taskId}
        className={MergeClassNames(
          'outline-none',
          TASK_BLOCK_CLASS,
          blockClassName,
          isTaskReordering && 'select-none'
        )}
      >
        {/* Stacked, the Task cell is a band of its own above the rows. */}
        {isStacked && <tr>{taskCell}</tr>}
        {rows.map((renderRow, index) =>
          renderRow(
            index === 0 && !isStacked ? taskCell : undefined,
            index === 0 ? notesCell : undefined
          )
        )}
      </tbody>
    );
  };

  // « / » is compact tasks on or off; a narrow sheet trims the column on
  // its own but keeps the details, so the button reflects the choice only.
  const trimLabel = isCompact
    ? TASKS_SHEET_EXPAND_TASK_COLUMN_LABEL
    : TASKS_SHEET_TRIM_TASK_COLUMN_LABEL;

  const taskHeaderCell = (rowSpan?: number) => (
    <th rowSpan={rowSpan} className={headerCellClass}>
      <span className="flex items-center justify-between gap-1">
        <span className="flex items-center gap-2">
          {TASKS_GRID_COLUMN_LABEL_TASK}
          {/* Named as well as shown: colour is never the only signal. */}
          {isActual && (
            <span className="rounded border border-gray-bright px-1.5 text-xs font-semibold text-white">
              {SUMMARY_DATA_BASIS_LABELS[SUMMARY_DATA_BASIS_ACTUAL]}
            </span>
          )}
        </span>
        <Tooltip title={trimLabel}>
          <button
            type="button"
            aria-label={trimLabel}
            aria-pressed={isCompact}
            className={MergeClassNames(
              'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded',
              isActual
                ? 'text-gray-bright hover:bg-white/10'
                : 'text-gray-dark hover:bg-gray-bright'
            )}
            onClick={() =>
              onTaskColumnWidthChange(isCompact ? 'full' : 'trimmed')
            }
          >
            {isCompact ? (
              <LuChevronsRight size={14} aria-hidden />
            ) : (
              <LuChevronsLeft size={14} aria-hidden />
            )}
          </button>
        </Tooltip>
      </span>
    </th>
  );

  const notesHeaderCell = notesAsColumn && (
    <th className={headerCellClass}>{TASKS_SHEET_NOTES_COLUMN_LABEL}</th>
  );

  const header = isProductionFilter ? (
    <thead>
      <tr>
        {!isStacked && taskHeaderCell(2)}
        <th rowSpan={2} className={headerCellClass}>
          {TASKS_SHEET_COLUMN_LABEL_TYPE}
        </th>
        <th colSpan={3} className={headerCellClass}>
          {PRODUCTION_MATRIX_COLUMN_LABEL_YIELD}
        </th>
        <th colSpan={3} className={headerCellClass}>
          {PRODUCTION_MATRIX_COLUMN_LABEL_PRICE}
        </th>
      </tr>
      <tr>
        {[0, 1].flatMap((group) =>
          [
            PRODUCTION_MATRIX_ROW_LABEL_LOW,
            PRODUCTION_MATRIX_ROW_LABEL_EXPECTED,
            PRODUCTION_MATRIX_ROW_LABEL_HIGH,
          ].map((label) => (
            <th key={`${group}-${label}`} className={headerCellClass}>
              {label}
            </th>
          ))
        )}
      </tr>
    </thead>
  ) : (
    <thead>
      <tr>
        {!isStacked && taskHeaderCell()}
        <th className={headerCellClass}>
          {TASKS_GRID_COLUMN_LABEL_INPUT_NAME}
        </th>
        <th className={headerCellClass}>{TASKS_GRID_COLUMN_LABEL_RATE}</th>
        {columns.rateUnit && (
          <th className={headerCellClass}>
            {TASKS_GRID_COLUMN_LABEL_RATE_UNIT}
          </th>
        )}
        {columns.required && (
          <th className={headerCellClass}>
            {TASKS_GRID_COLUMN_LABEL_REQUIRED}
          </th>
        )}
        {columns.costPerUnit && (
          <th className={headerCellClass}>
            {TASKS_GRID_COLUMN_LABEL_COST_PER_UNIT}
          </th>
        )}
        {columns.costPerHa && (
          <th className={MergeClassNames(headerCellClass, 'text-right')}>
            {TASKS_GRID_COLUMN_LABEL_COST_PER_HA}
          </th>
        )}
        {notesHeaderCell}
      </tr>
    </thead>
  );

  // Widths match SHEET_COLUMN_REM (sheetLayout.ts).
  const taskColumnClass = isTrimmed ? 'w-[13rem]' : 'w-[18rem]';
  // Wide, the sheet stops growing so numbers stay beside their product:
  // with Notes as a column it takes what Input name (28 rem) doesn't; without,
  // Input name is the flexible column and the cap holds it near 28 rem.
  const tableWidthClass = notesAsColumn
    ? 'max-w-[112rem]'
    : isProductionFilter
      ? 'max-w-[88rem]'
      : 'max-w-[76rem]';

  useEffect(() => {
    onMaxWidthClassChange?.(tableWidthClass);
  }, [onMaxWidthClassChange, tableWidthClass]);

  return (
    // Edge to edge: the row controls sit inside the cells, so the sheet
    // needs no side lanes.
    <div
      className={MergeClassNames(
        'task-sheet bg-white',
        isActual && 'task-sheet--actual'
      )}
    >
      <table
        className={MergeClassNames(
          'w-full table-fixed border-collapse text-base text-gray-dark',
          tableWidthClass
        )}
      >
        {isProductionFilter ? (
          <colgroup>
            {!isStacked && <col className={taskColumnClass} />}
            <col />
            {PRODUCTION_SHEET_COLUMNS.map((column) => (
              <col key={column.key} className="w-[6.5rem]" />
            ))}
          </colgroup>
        ) : (
          <colgroup>
            {!isStacked && <col className={taskColumnClass} />}
            <col className={notesAsColumn ? 'w-[28rem]' : undefined} />
            <col className="w-[5rem]" />
            {columns.rateUnit && <col className="w-[6rem]" />}
            {columns.required && <col className="w-[6rem]" />}
            {columns.costPerUnit && <col className="w-[6rem]" />}
            {columns.costPerHa && <col className="w-[6.5rem]" />}
            {notesAsColumn && <col />}
          </colgroup>
        )}
        {header}
        {planTasks.length === 0 && (
          <tbody>
            <tr>
              <td
                colSpan={columnCount}
                className="border border-gray-medium bg-white px-3 py-3 text-sm text-gray-dark"
              >
                {isAll
                  ? emptyState
                  : TASKS_SHEET_EMPTY_PLAN(activeFilter.label)}
              </td>
            </tr>
          </tbody>
        )}
        {planTasks.map(renderPlanTask)}
        {missingTasks.length > 0 && (
          <tbody>
            <tr>
              <td
                colSpan={columnCount}
                className="border border-gray-medium bg-secondary-light p-0"
              >
                <button
                  type="button"
                  aria-expanded={showMissing}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm font-semibold text-charcoal hover:bg-gray-bright"
                  onClick={() => setIsMissingOpen(!showMissing)}
                >
                  <FaCircleChevronRight
                    size={14}
                    aria-hidden="true"
                    className={MergeClassNames(
                      'flex-shrink-0 text-secondary-dark transition-transform duration-150',
                      showMissing ? 'rotate-90' : 'rotate-0'
                    )}
                  />
                  {`${TASKS_SHEET_WITHOUT_PREFIX} ${activeFilter.label.toLowerCase()}`}
                  <span className="font-normal text-gray-dark">
                    ({missingTasks.length})
                  </span>
                </button>
              </td>
            </tr>
            {showMissing &&
              missingTasks.map((task) => renderEmptyTaskRow(task, true))}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default TaskCategorySheet;
